<template>
  <v-dialog v-model="isDistributionDialogVisible" width="1000">
    <v-card>
      <v-card-title
        class="font-weight-bold d-flex justify-space-between align-center"
      >
        <span>{{ $t('distribution.title') }}</span>
        <v-btn
          icon
          small
          @click="isDistributionDialogVisible = false"
          class="ml-2"
        >
          <v-icon size="20">mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        {{ $t('distribution.description') }}
      </v-card-text>
      <v-card-text v-if="isLoading">
        <div class="d-flex flex-column pa-3 text-center" style="gap: 8px">
          <div class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div class="text-body2">{{ $t('distribution.loading') }}</div>
        </div>
      </v-card-text>
      <v-card-text v-else-if="classCodeError">
        <div class="d-flex flex-column pa-3 text-center" style="gap: 8px">
          <v-icon color="error" size="48">mdi-alert-circle</v-icon>
          <div class="text-body2 text-error">
            {{ classCodeError }}
          </div>
        </div>
      </v-card-text>
      <v-card-text v-else>
        <v-row>
          <v-col cols="12" class="pa-1">
            <tickets-overview
              :used-count="usedCouponCount"
              :total-count="totalCouponCount"
              :unused-count="unusedCouponCount"
              :is-loading="isLoadingCoupons"
              :error="ticketsOverviewError"
              @see-tickets="handleSeeTickets"
            />
          </v-col>
          <v-col cols="6" class="pa-1">
            <class-code :class-code="classCode" />
          </v-col>
          <v-col cols="6" class="pa-1">
            <direct-link :class-code="classCode" />
          </v-col>
          <v-col cols="12" class="pa-1">
            <qr-code :class-code="classCode" />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent } from "@vue/composition-api";
import { packageService } from "@/api/packageService";
import type { PackageWithId } from "@/resources/package";
import { getCouponList } from "@/utils";
import ClassCode from "./ClassCode.vue";
import DirectLink from "./DirectLink.vue";
import QrCode from "./QrCode.vue";
import TicketsOverview from "./TicketsOverview.vue";

export default defineComponent({
  name: "DistributionDialog",
  components: {
    ClassCode,
    DirectLink,
    QrCode,
    TicketsOverview,
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    package: {
      type: Object as () => PackageWithId | null,
      required: true,
    },
  },
  data() {
    return {
      classCode: undefined as string | undefined,
      isLoading: false,
      classCodeError: undefined as string | undefined,
      totalCouponCount: 0,
      usedCouponCount: 0,
      unusedCouponCount: 0,
      isLoadingCoupons: false,
      ticketsOverviewError: undefined as string | undefined,
    };
  },
  computed: {
    isDistributionDialogVisible: {
      get(): boolean {
        return this.isVisible;
      },
      set(value: boolean) {
        this.$emit("update:isVisible", value);
      },
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      async handler(newValue: boolean) {
        if (newValue && this.package?.id) {
          await Promise.all([this.fetchClassCode(), this.fetchCouponCounts()]);
        }
      },
    },
  },
  methods: {
    async fetchClassCode() {
      if (!this.package?.id) {
        this.classCodeError = this.$t(
          "distribution.errors.packageNotFound",
        ) as string;
        return;
      }

      if (this.package.classCode) {
        this.classCode = this.package.classCode;
        return;
      }

      this.isLoading = true;
      this.classCodeError = undefined;
      this.classCode = undefined;

      try {
        const classCode = await packageService.getClassCode(this.package.id);
        if (classCode) {
          this.classCode = classCode;
        } else {
          this.classCodeError = this.$t(
            "distribution.errors.classCode",
          ) as string;
        }
      } catch (error) {
        console.error("Error fetching class code:", error);
        this.classCodeError = this.$t(
          "distribution.errors.classCode",
        ) as string;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchCouponCounts() {
      if (!this.package?.id) return;

      this.isLoadingCoupons = true;
      this.ticketsOverviewError = undefined;
      this.totalCouponCount = 0;
      this.usedCouponCount = 0;
      this.unusedCouponCount = 0;

      try {
        const couponList = await getCouponList("packages", this.package.id);
        // getCouponList doesn't throw on failure — it returns a zero-filled
        // object. Detect failure two ways: empty `code` means the package
        // lookup failed, and a mismatch between `totalCouponCount` and
        // `coupons.length` means the coupon fetch bailed early.
        const fetchFailed =
          !couponList.code ||
          couponList.totalCouponCount !== couponList.coupons.length;
        if (fetchFailed) {
          this.ticketsOverviewError = this.$t(
            "distribution.errors.ticketsOverview",
          ) as string;
          return;
        }
        this.totalCouponCount = couponList.totalCouponCount;
        this.usedCouponCount = couponList.usedCouponCount;
        this.unusedCouponCount = couponList.unusedCouponCount;
      } catch (error) {
        console.error("Error fetching coupon counts:", error);
        this.ticketsOverviewError = this.$t(
          "distribution.errors.ticketsOverview",
        ) as string;
      } finally {
        this.isLoadingCoupons = false;
      }
    },
    handleSeeTickets() {
      if (!this.package?.id) return;
      this.$emit("see-tickets", { packageId: this.package.id });
      this.isDistributionDialogVisible = false;
    },
  },
});
</script>
